import { createContext, useContext, useEffect, useState } from "react";
import { Globe, ChevronDown } from "lucide-react";
import { DICT } from "./translations";

const LanguageContext = createContext(null);
export const useLanguage = () => useContext(LanguageContext);

let CURRENT_LANG = "en";
export const tr = (s) => (typeof s === "string" && DICT[CURRENT_LANG] && DICT[CURRENT_LANG][s]) || s;

export const LANGS = { en: "English", hi: "हिन्दी", gu: "ગુજરાતી" };

const T = {
  en: {
    nav: {
      Home: "Home",
      "Why DWC": "Why DWC",
      "Driving Psychology": "Driving Psychology",
      Programs: "Programs",
      "Success Stories": "Success Stories",
      Services: "Services",
      Guarantee: "Guarantee",
      Franchise: "Franchise",
      Blog: "Blog",
      Contact: "Contact",
    },
    book: "Book an Appointment",
    bookShort: "Book an Appointment",
    whatsapp: "Chat on WhatsApp",
    heroLines: ["Driving Isn't Just", "About the Car.", "It's About the", "Driver's Mind."],
    heroSub:
      "Learning to drive is one thing. Driving with confidence is another. At DWC, we combine practical driving training with structured learning and driving psychology to help learners overcome fear, improve their reactions and decisions, and become confident, independent drivers.",
  },
  hi: {
    nav: {
      Home: "होम",
      "Why DWC": "DWC क्यों?",
      "Driving Psychology": "ड्राइविंग साइकोलॉजी",
      Programs: "प्रोग्राम्स",
      "Success Stories": "सफलता की कहानियाँ",
      Services: "सेवाएँ",
      Guarantee: "गारंटी",
      Franchise: "फ्रैंचाइज़ी",
      Blog: "ब्लॉग",
      Contact: "संपर्क करें",
    },
    book: "अपॉइंटमेंट बुक करें",
    bookShort: "अपॉइंटमेंट बुक करें",
    whatsapp: "व्हाट्सऐप पर चैट करें",
    heroLines: ["गाड़ी चलाना सिर्फ़", "गाड़ी के बारे में नहीं।", "यह ड्राइवर के", "मन के बारे में है।"],
    heroSub:
      "गाड़ी चलाना सीखना एक बात है। आत्मविश्वास के साथ गाड़ी चलाना दूसरी। DWC में हम प्रैक्टिकल ड्राइविंग ट्रेनिंग को स्ट्रक्चर्ड लर्निंग और ड्राइविंग साइकोलॉजी के साथ जोड़ते हैं — ताकि लर्नर डर पर काबू पाएँ, अपने रिएक्शन और निर्णय बेहतर बनाएँ, और आत्मविश्वासी, स्वतंत्र ड्राइवर बनें।",
  },
  gu: {
    nav: {
      Home: "હોમ",
      "Why DWC": "DWC શા માટે?",
      "Driving Psychology": "ડ્રાઇવિંગ સાયકોલોજી",
      Programs: "પ્રોગ્રામ્સ",
      "Success Stories": "સફળતાની વાર્તાઓ",
      Services: "સેવાઓ",
      Guarantee: "ગેરંટી",
      Franchise: "ફ્રેન્ચાઇઝી",
      Blog: "બ્લોગ",
      Contact: "સંપર્ક કરો",
    },
    book: "એપોઇન્ટમેન્ટ બુક કરો",
    bookShort: "એપોઇન્ટમેન્ટ બુક કરો",
    whatsapp: "વોટ્સએપ પર ચેટ કરો",
    heroLines: ["ડ્રાઇવિંગ ફક્ત", "કાર વિશે નથી.", "તે ડ્રાઇવરના", "મન વિશે છે."],
    heroSub:
      "ડ્રાઇવિંગ શીખવું એક વાત છે. આત્મવિશ્વાસ સાથે ડ્રાઇવ કરવું બીજી. DWC માં અમે પ્રેક્ટિકલ ડ્રાઇવિંગ ટ્રેનિંગને સ્ટ્રક્ચર્ડ લર્નિંગ અને ડ્રાઇવિંગ સાયકોલોજી સાથે જોડીએ છીએ — જેથી લર્નર ડર પર કાબૂ મેળવે, પોતાના રિએક્શન અને નિર્ણય સુધારે, અને આત્મવિશ્વાસુ, સ્વતંત્ર ડ્રાઇવર બને.",
  },
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("dwc-lang") || "en";
    } catch {
      return "en";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("dwc-lang", lang);
    } catch { /* ignore */ }
  }, [lang]);
  CURRENT_LANG = lang;
  return (
    <LanguageContext.Provider value={{ lang, setLang, t: T[lang] || T.en, tr }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const LanguageMenu = ({ full = false }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="language-select-btn"
        aria-label="Change language"
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-2 text-xs font-extrabold uppercase tracking-wide text-dwc-charcoal/70 transition-colors hover:border-dwc-green/40 hover:text-dwc-green ${full ? "w-full justify-center py-3" : ""}`}
      >
        <Globe className="h-4 w-4 text-dwc-green" aria-hidden="true" />
        {LANGS[lang]}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-40 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-[0_16px_40px_rgba(0,0,0,0.12)]" data-testid="language-menu">
          {Object.entries(LANGS).map(([code, label]) => (
            <button
              key={code}
              type="button"
              data-testid={`language-option-${code}`}
              onClick={() => {
                setLang(code);
                setOpen(false);
              }}
              className={`block w-full px-4 py-2.5 text-left text-sm font-semibold transition-colors ${
                lang === code ? "bg-dwc-soft text-dwc-green" : "text-dwc-charcoal/75 hover:bg-dwc-soft"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
